import type { RecurrenceRule } from './types';

/**
 * Pure date helpers — no timezone, ISO YYYY-MM-DD strings only.
 * All dates are treated as calendar dates in the user's locale.
 */

function parseDate(iso: string): { year: number; month: number; day: number } {
  const [y, m, d] = iso.split('-').map(Number);
  return { year: y!, month: m!, day: d! };
}

function formatDate(year: number, month: number, day: number): string {
  const mm = String(month).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `${year}-${mm}-${dd}`;
}

function daysInMonth(year: number, month: number): number {
  // month: 1..12
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

function addDays(iso: string, days: number): string {
  const { year, month, day } = parseDate(iso);
  const dt = new Date(Date.UTC(year, month - 1, day));
  dt.setUTCDate(dt.getUTCDate() + days);
  return formatDate(dt.getUTCFullYear(), dt.getUTCMonth() + 1, dt.getUTCDate());
}

function addMonths(iso: string, months: number, dayOfMonth: number | null): string {
  const { year, month, day } = parseDate(iso);
  const targetMonthIndex = month - 1 + months; // 0-based month
  const newYear = year + Math.floor(targetMonthIndex / 12);
  const newMonth = ((targetMonthIndex % 12) + 12) % 12 + 1; // 1..12
  const desiredDay = dayOfMonth ?? day;
  const clampedDay = Math.min(desiredDay, daysInMonth(newYear, newMonth));
  return formatDate(newYear, newMonth, clampedDay);
}

/**
 * Compute the next occurrence date strictly after `from`.
 *
 * - weekly: from + 7 * interval days
 * - monthly/quarterly/yearly: shift months, snap to dayOfMonth (clamped to month length).
 *   For yearly with interval=1 we shift by 12 months so a Feb-29 start lands on Feb-28
 *   on non-leap years.
 */
export function computeNextOccurrence(rule: RecurrenceRule, from: string): string {
  const interval = Math.max(1, Math.floor(rule.interval));
  switch (rule.frequency) {
    case 'weekly':
      return addDays(from, 7 * interval);
    case 'monthly':
      return addMonths(from, interval, rule.dayOfMonth);
    case 'quarterly':
      return addMonths(from, 3 * interval, rule.dayOfMonth);
    case 'yearly':
      return addMonths(from, 12 * interval, rule.dayOfMonth);
  }
}

/** True iff `a <= b` for ISO YYYY-MM-DD dates. */
export function isOnOrBefore(a: string, b: string): boolean {
  return a <= b;
}

/** True iff `from..to` (inclusive) covers `date`. endDate=null means open-ended. */
export function isInRange(date: string, startDate: string, endDate: string | null): boolean {
  if (date < startDate) return false;
  if (endDate && date > endDate) return false;
  return true;
}
